import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import type { RouterOutputs } from "@alm/api-contracts";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { Settings } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZoneFor } from "@/lib/facility/zone";
import { VisitorNav } from "../-components/visitor-nav";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/departments/")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorDepartmentsPage,
});

type TrafficRow = RouterOutputs["visitors"]["departments"]["traffic"][number];

const MAX_COMPANIES = 4;

function columns(zoneFor: (facilityId?: string | null) => string): ColumnDef<TrafficRow>[] {
  return [
    {
      id: "department",
      header: "Department",
      accessorKey: "departmentName",
      enableHiding: false,
      meta: { className: "min-w-48" },
      cell: ({ row: { original: department } }) => (
        <span className="flex items-center gap-2">
          <Link
            to="/visitors/departments/$departmentId"
            params={{ departmentId: department.departmentId }}
            className="focus-ring rounded-sm font-medium hover:underline"
          >
            {department.departmentName}
          </Link>
          {department.isActive ? null : <Badge variant="neutral">Archived</Badge>}
        </span>
      ),
    },
    {
      id: "visits",
      header: "Visits",
      accessorKey: "visits",
      meta: { nowrap: true, align: "right" },
      cell: ({ row: { original: department } }) => (
        <span className="tabular-nums">{department.visits}</span>
      ),
    },
    {
      id: "people",
      header: "People",
      accessorKey: "people",
      meta: { nowrap: true, align: "right", priority: 2 },
      cell: ({ row: { original: department } }) => (
        <span className="tabular-nums">{department.people}</span>
      ),
    },
    {
      id: "companies",
      header: "Visiting from",
      accessorFn: (department) => department.companies.map((company) => company.name).join(", "),
      enableSorting: false,
      meta: { clamp: "lg", lines: 2 },
      cell: ({ row: { original: department } }) =>
        department.companies.length === 0 ? (
          <span className="text-muted-foreground">-</span>
        ) : (
          <span className="flex flex-wrap gap-1.5">
            {department.companies.slice(0, MAX_COMPANIES).map((company) => (
              <span
                key={company.name}
                className="bg-muted inline-flex items-center gap-1 rounded-xs px-2 py-0.5 text-xs"
              >
                {company.name}
                <span className="text-muted-foreground tabular-nums">{company.visits}</span>
              </span>
            ))}
            {department.companies.length > MAX_COMPANIES ? (
              <span className="text-muted-foreground text-xs">
                +{department.companies.length - MAX_COMPANIES} more
              </span>
            ) : null}
          </span>
        ),
    },
    {
      id: "last",
      header: "Last visit",
      accessorFn: (department) =>
        department.lastVisitAt ? new Date(department.lastVisitAt).getTime() : 0,
      meta: { nowrap: true },
      cell: ({ row: { original: department } }) => (
        <RelativeTime value={department.lastVisitAt} timeZone={zoneFor(department.facilityId)} />
      ),
    },
  ];
}

/** Who is visiting each part of the building, and from which companies. */
function VisitorDepartmentsPage() {
  const { facilityId } = useFacility();
  const zoneFor = useZoneFor();
  const canManage = useCan("visitor:manage");
  const traffic = useQuery(
    orpc.visitors.departments.traffic.queryOptions({ input: { facilityId } }),
  );
  const rows = traffic.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Departments"
        count={traffic.isSuccess ? rows.length : undefined}
        meta="Visits that were for the building rather than a resident, by the department they went to."
        actions={
          canManage ? (
            <Button asChild variant="outline">
              <Link to="/settings/visitor-kiosk">
                <Settings data-icon="inline-start" aria-hidden="true" />
                Manage departments
              </Link>
            </Button>
          ) : null
        }
      />
      <VisitorNav />
      {traffic.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the departments."
            error={traffic.error}
            onRetry={() => void traffic.refetch()}
            retrying={traffic.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={columns(zoneFor)}
          noun="departments"
          rowId={(department) => department.departmentId}
          pending={traffic.isPending}
          initialSorting={[{ id: "visits", desc: true }]}
          empty={
            <EmptyState
              title="No departments yet"
              description="Name the building's departments under Settings, Visitor kiosk, and the kiosk offers them to visitors who are not here for a resident."
            />
          }
        />
      )}
    </div>
  );
}
